import React from "react";
import { DrawerBody, DrawerHeader, DrawerHeaderTitle, Tooltip, Button, Drawer, } from "@fluentui/react-components";
import { DismissRegular } from "@fluentui/react-icons";
import classNames from "owa-classnames";
import { isCapabilityEnabled } from "owa-capabilities";
import { bleedThroughCapability } from "owa-capabilities-definitions/lib/bleedThroughCapability";
import { observer } from "owa-mobx-react";
import ProjectionContext from "owa-popout-v2/lib/context/ProjectionContext";
import { createPortal } from "owa-react-dom";
import { ResizeHandle, ResizeHandleDirection } from "owa-resize-handle";
import { closeDrawerPane } from "../orchestration/closeDrawerPane";
import { isDrawerPaneVisible } from "../selectors/isDrawerPaneVisible";
import { DEFAULT_DRAWER_PANE_WIDTH } from "../constants/constants";
import { drawerPaneWidthChanged } from "../actions/drawerPaneWidthChanged";
import type DrawerPaneProps from "../store/schema/DrawerPaneProps";
import isPopoutWithoutHeader from "../utils/isPopoutWithoutHeader";
import { drawerContainer, drawerContainerContained, drawerContainerInResize, drawerContainerPopout, drawerHeader, drawerBody, fullBleed, hiddenContent, noPaddingBody, noPaddingHeader, resizeSpacer, resizeWrapper, resizeWrapperContained, resizeWrapperPopout, closeButton, } from "./DrawerPane.scss";
import loc from "owa-localize";
import { closeTooltip } from "./DrawerPane.locstring.json";
import { focusRibbonButton } from "../utils/drawerFocusUtils";
import { useRegisterNavigationLoopContainer } from "owa-accessibility-loop-nav/lib/hooks/useRegisterNavigationLoopContainer";

const dismissIcon = <DismissRegular />;
const resizeHandleStyle: React.CSSProperties = { pointerEvents: "auto" } as const;
const MAX_WIDTH_RATIO = 0.6;

export const DrawerPane: React.FunctionComponent<DrawerPaneProps> = observer(function DrawerPane({ paneId, children, headerText, showCloseButton = true, className, headerClassName, titleClassName, bodyClassName, mountNode, type, preserveContentOnDismiss = false, noPadding, noHeaderPadding, preload = false, role = "dialog", width = DEFAULT_DRAWER_PANE_WIDTH, resize, resizeWrapperStyle, }: DrawerPaneProps) {
    const parentNodeRef = React.useRef<HTMLElement | null>(null);
    const containerRef = React.useRef<HTMLDivElement | null>(null);
    const hasBeenOpenedRef = React.useRef(false);
    const targetWindow = (React.useContext(ProjectionContext) ?? window) as Window;
    const isOpen = isDrawerPaneVisible(paneId, targetWindow);
    const drawerStyle = React.useMemo(() => ({ width: `${width}px` }), [width]);
    
    // Sync the prop-driven width to the store so layout orchestrators can read it.
    // useLayoutEffect fires before paint, ensuring layout calculations (calculateAvailableWidthBucket,
    // updateFlexPaneInternalWidth) see the correct width within the same frame.
    React.useLayoutEffect(() => {
        if (isOpen) {
            drawerPaneWidthChanged(paneId, width, targetWindow);
        }
    }, [isOpen, paneId, width, targetWindow, mountNode]);
    
    // If the pane is open for the first time OR preloaded, we need to set the parentNodeRef.
    // We cache the resolved mount node so content can be preserved across dismiss/reopen, but
    // it must be re-resolved when the target changes (e.g. a different calendar event form
    // registers a new anchor). Otherwise the pane would portal into a stale element and appear
    // to vanish (the underlay still reserves slot width, so only the drawer disappears).
    if (isOpen || preload) {
        const cachedNode = parentNodeRef.current;
        const nextNode = mountNode ?? targetWindow.document.body;
        const shouldUpdateMountNode = !cachedNode || nextNode !== cachedNode;
        if (shouldUpdateMountNode) {
            // If mountNode is not provided, try to mount to the mainApp element in the target window
            // Otherwise, renders on top of the whole page
            parentNodeRef.current = nextNode;
        }
        
        // Mark that the pane has been opened at least once
        hasBeenOpenedRef.current = true;
    }
    
    const handleCloseDrawerPane = React.useCallback(() => {
        closeDrawerPane(paneId, targetWindow);
        focusRibbonButton(paneId);
    }, [paneId, targetWindow]);
    
    const handleResized = React.useCallback((_prevDimension: number, newDimension: number) => {
        resize?.onResize?.(newDimension);
    }, [resize?.onResize]);
    
    const dismissButton = React.useMemo(() => {
        if (showCloseButton) {
            return (<Tooltip content={loc(closeTooltip)} relationship="label" positioning="below-start">
                    <Button appearance="subtle" className={closeButton} icon={dismissIcon} onClick={handleCloseDrawerPane}/>
                </Tooltip>);
        }
        return null;
    }, [showCloseButton, handleCloseDrawerPane]);
    
    // If we're preserving content, only render if the drawer has been opened at least once
    // Otherwise, render only when the drawer is currently open
    // Bypass all of this if preload is true, as we want to render the drawer pane on load
    const shouldRenderDrawerPane = preserveContentOnDismiss ? hasBeenOpenedRef.current : isOpen;
    
    useRegisterNavigationLoopContainer("DrawerPane", containerRef, false /* shouldSkipAdding */, [
        preload,
        isOpen,
        preserveContentOnDismiss,
    ]);
    
    if (!preload && !shouldRenderDrawerPane) {
        return null;
    }
    
    const drawerElement = (<Drawer ref={containerRef} data-app-section={"DrawerPane"} role={role} open={shouldRenderDrawerPane} 
    // When using ResizeHandle, the Drawer is rendered in a portal,
    // so we don't set mountNode to ensure it's a sibling of the ResizeHandle.
    // Otherwise, we use the provided mountNode (or default to body).
    mountNode={resize ? undefined : parentNodeRef.current} modalType="non-modal" position="end" className={classNames(drawerContainer, mountNode && drawerContainerContained, isPopoutWithoutHeader(targetWindow) && drawerContainerPopout, isCapabilityEnabled(bleedThroughCapability) && fullBleed, preserveContentOnDismiss && !isOpen && hiddenContent, resize && drawerContainerInResize, className)} style={drawerStyle} 
    // Disabling for now the default motion because of this bug:
    // https://github.com/microsoft/fluentui/issues/33583
    surfaceMotion={null} 
    // If resize config is provided, use 'inline' type to ensure the
    // drawer is a sibling of the ResizeHandle.
    // Otherwise, use the provided type (defaulting to 'modal')
    type={resize ? "inline" : type} {...(resize && {
        "data-min-width": resize.minWidth ?? DEFAULT_DRAWER_PANE_WIDTH,
        "data-max-width": resize.maxWidth ?? MAX_WIDTH_RATIO,
    })}>
            <DrawerHeader className={classNames(noHeaderPadding ? noPaddingHeader : drawerHeader, headerClassName)}>
                <DrawerHeaderTitle className={titleClassName} action={dismissButton}>
                    {headerText}
                </DrawerHeaderTitle>
            </DrawerHeader>
            <DrawerBody className={classNames(noPadding ? noPaddingBody : drawerBody, bodyClassName)}>
                {children}
            </DrawerBody>
        </Drawer>);
    
    // When resize config is provided, wrap in a container with ResizeHandle + InlineDrawer
    // as true DOM siblings. The spacer gives ResizeHandle a valid previousSibling for
    // offset calculations. pointer-events toggling ensures the spacer is click-transparent.
    if (resize && isOpen && parentNodeRef.current) {
        return createPortal(<div style={resizeWrapperStyle} className={classNames(resizeWrapper, mountNode && resizeWrapperContained, isPopoutWithoutHeader(targetWindow) && resizeWrapperPopout)}>
                <div className={resizeSpacer}/>
                {!resize.disabled && (<ResizeHandle style={resizeHandleStyle} direction={ResizeHandleDirection.vertical} onResized={handleResized} scenario={resize.scenario}/>)}
                {drawerElement}
            </div>, parentNodeRef.current);
    }
    
    return drawerElement;
}, "DrawerPane");
